/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box, Paper } from "@mui/material";
import nameMismatchImage from "../../assets/images/ClipboardList.svg";
import exactMatch from "../../assets/images/UsersRound.svg"
import createdImage from "../../assets/images/UserRoundCheck.svg";
import totalMismatchImage from "../../assets/images/Table.svg"
import phoneImage from "../../assets/images/Smartphone.svg";
import {
  StatisticsTitle,
  StatisticsValue,
  CardContainer,
  IconContainer,
  InformationContainer,
  TitleContainer,
  LightTooltip,
  StyledInfoIcon,
} from "../statistics_cards/styles";
import { StatisticSkeletalLoader } from "../statistic_skeletal_loader";
const BORDER_COLOR = "#eee";

const STATISTICS_CONFIG = [
  {
    key: "auto_merged",
    title: "Exact Match",
    description: "Exact match found during import",
    icon: <img src={exactMatch} alt="Exact Match" />,
    formatValue: (value) => value?.toString() ?? "0",
    card_value: 'exact_match',
  },
  {
    key: 'new_created',
    title: 'Created',
    description: 'Created during import',
    icon: <img src={createdImage} alt="Created" />,
    formatValue: (value) => value?.toString() ?? "0",
    card_value: 'new_created',
  },
  {
    key: "total_mismatches",
    title: "All Mismatches",
    description: "All mismatches found during import",
    icon: <img src={totalMismatchImage} alt="All Match" />,
    formatValue: (value) => value?.toString() ?? "0",
    card_value: 'total_mismatches',
  },
  {
    key: "phone_mismatches",
    title: "Phone Mismatch",
    description: "Phone mismatch found during import",
    icon: <img src={phoneImage} alt="Phone Mismatch" />,
    formatValue: (value) => value?.toString() ?? "0",
    card_value: 'phone_mismatch',
  },
  {
    key: "phone_missing",
    title: "Phone Missing",
    description: "Missing phone found during import",
    icon: <img src={phoneImage} alt="Phone Missing" />,
    formatValue: (value) => value?.toString() ?? "0",
    card_value: 'phone_missing',
  },
  {
    key: "name_mismatches",
    title: "Name Mismatch",
    description: "Name mismatch found during import",
    icon: <img src={nameMismatchImage} alt="Name Mismatch" />,
    formatValue: (value) => value?.toString() ?? "0",
    card_value: 'name_mismatch',
  },
];

const LAYOUTS = {
  sm: [2, 2, 2],
  lg: [3, 3],
};

const getCardBorders = (index, layout) => {
  let row = 0;
  let offset = 0;

  for (let r = 0; r < layout.length; r++) {
    if (index < offset + layout[r]) {
      row = r;
      break;
    }
    offset += layout[r];
  }

  const positionInRow = index - offset;
  const cardsInRow = layout[row];
  const isFirstRow = row === 0;
  const isLastRow = row === layout.length - 1;
  const isFirstInRow = positionInRow === 0;
  const isLastInRow = positionInRow === cardsInRow - 1;

  return {
    borderTop:    isFirstRow    ? `1px solid ${BORDER_COLOR}` : "none",
    borderBottom: isLastRow     ? `1px solid ${BORDER_COLOR}` : `1px solid ${BORDER_COLOR}`,
    borderLeft:   isFirstInRow  ? `1px solid ${BORDER_COLOR}` : "none",
    borderRight:  isLastInRow   ? `1px solid ${BORDER_COLOR}` : `1px solid ${BORDER_COLOR}`,
    borderTopLeftRadius:     isFirstRow && isFirstInRow ? 10 : 0,
    borderTopRightRadius:    isFirstRow && isLastInRow  ? 10 : 0,
    borderBottomLeftRadius:  isLastRow  && isFirstInRow ? 10 : 0,
    borderBottomRightRadius: isLastRow  && isLastInRow  ? 10 : 0,
  };
};

export const ImportLogMetric = ({
  isMobile, data = {},
  loading = false,
  CLICKABLE_CARDS = [],
  selectedCard,
  handleCardClick = () => {}
}) => (
  <Box
    data-testid="import-log-metrics-container"
    sx={{
      display: "grid",
      margin: "20px 0px",
      borderRadius: "10px",
      gridTemplateColumns: "repeat(2, 1fr)",
      "@media (min-width: 1000px)": {
        gridTemplateColumns: "repeat(3, 1fr)",
      },
    }}
  >
    {STATISTICS_CONFIG.map((stat, index) => {
      const value = data?.[stat.key];
      const formattedValue = stat.formatValue(value);
      const isClickable = CLICKABLE_CARDS.includes(stat.card_value);
      const isSelected = isClickable && selectedCard === stat.card_value;

      const smBorders = getCardBorders(index, LAYOUTS.sm);
      const lgBorders = getCardBorders(index, LAYOUTS.lg);

      return (
        <Paper
          key={stat.key}
          elevation={0}
          square
          data-testid={`import-log-card-${stat.key}`}
          onClick={() => handleCardClick(stat.card_value)}
          sx={{
            minHeight: 73,
            padding: isMobile ? "12px" : "5px 20px",
            backgroundColor: isSelected ? '#fff8f6' : 'white',
            boxShadow: isSelected ? 'inset 0 0 0 1px #ff653a' : 'none',
            transition: "background-color 0.15s ease, box-shadow 0.15s ease",
            ...smBorders,
            "@media (min-width: 1000px)": {
              ...lgBorders,
            },
            cursor: isClickable ? 'pointer' : 'default',
            '&:hover': isClickable && stat.card_value !== selectedCard ? {
              backgroundColor: '#fafcff',
              boxShadow: 'inset 0 0 0 1px #d7e3ff',
            } : undefined,
          }}
        >
          <CardContainer isMobile={isMobile}>
            <IconContainer>{stat.icon}</IconContainer>
            <InformationContainer isMobile={isMobile}>
              <TitleContainer>
                <StatisticsTitle data-testid={`import-log-title-${stat.key}`}>
                  {stat.title}
                </StatisticsTitle>
                <LightTooltip title={stat.description} placement="top">
                  <StyledInfoIcon />
                </LightTooltip>
              </TitleContainer>
              {loading ? (
                <StatisticSkeletalLoader />
              ) : (
                <StatisticsValue data-testid={`import-log-value-${stat.key}`}>
                  {formattedValue}
                </StatisticsValue>
              )}
            </InformationContainer>
          </CardContainer>
        </Paper>
      );
    })}
  </Box>
);
